// VERSÃO 12.0 SUPREMA - TRIAGEM TÁTICA & BUSCA DE DUPLICIDADE (TOTAL SYNC)

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import MainLayout from '../components/layout/MainLayout';
import MobileGuard from '../components/guards/MobileGuard';
import ImovelCard from '../components/imovel/ImovelCard';
import { 
    FiClock, FiCheckCircle, FiLoader, FiAlertTriangle, 
    FiZap, FiShield, FiUsers, FiCpu, FiLayers, FiChevronDown, FiSearch, FiActivity 
} from 'react-icons/fi';
import apiClient from '../api/apiClient';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-toastify';

// MODAIS SUPREMOS
import ImovelEditorModal from '../components/modals/ImovelEditorModal';
import PreviewVitrinePremiumModal from '../components/modals/PreviewVitrinePremiumModal';

export default function ImoveisAprovacaoPage() {
    const [imoveis, setImoveis] = useState([]);
    const [entities, setEntities] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [userRole, setUserRole] = useState(null); 
    const [selectedEntity, setSelectedEntity] = useState('todos');
    
    // --- ESTADOS DE BUSCA ---
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('todos');
    const [statusFilter, setStatusFilter] = useState('Pendente'); 
    
    const [imovelAlvoId, setImovelAlvoId] = useState(null);
    const [imovelAlvoObj, setImovelAlvoObj] = useState(null);
    const [isEditorModalOpen, setIsEditorModalOpen] = useState(false);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decodedToken = jwtDecode(token);
                setUserRole(decodedToken.role?.toUpperCase() || 'USER'); 
            } catch (e) { console.error("Erro patente."); }
        }
    }, []);

    useEffect(() => {
        if (['MASTER', 'GESTOR', 'ADM'].includes(userRole)) {
            apiClient.get('/api/dashboard/entities')
                .then(res => setEntities(res.data))
                .catch(err => console.error("Erro unidades:", err));
        }
    }, [userRole]);

    // 🚀 MOTOR DE EXTRAÇÃO DE ALTA DEFINIÇÃO (AUDIT ENGINE)
    const fetchImoveis = useCallback(async () => {
        if (!userRole) return;
        setIsLoading(true);
        try {
            const baseParams = {};
            if (['MASTER', 'ADM'].includes(userRole) && selectedEntity !== 'todos') {
                baseParams.entityId = selectedEntity;
            }

            let finalData = [];

            if (statusFilter === 'todos') {
                // 📡 SCANNER DE DUPLICIDADE: Dispara buscas paralelas para garantir "Pendente" + "Aprovado"
                const [respPendentes, respAprovados] = await Promise.all([
                    apiClient.get('/api/filtros/busca', { params: { ...baseParams, status: 'Pendente' } }),
                    apiClient.get('/api/filtros/busca', { params: { ...baseParams, status: 'Aprovado' } })
                ]);
                
                // Unifica os arrays e remove possíveis duplicatas de ID (Safety First)
                const combined = [...respPendentes.data, ...respAprovados.data];
                finalData = Array.from(new Map(combined.map(item => [item._id, item])).values());
            } else {
                // 🎯 BUSCA DIRECIONADA
                const response = await apiClient.get('/api/filtros/busca', { params: { ...baseParams, status: statusFilter } });
                finalData = response.data;
            }

            setImoveis(Array.isArray(finalData) ? finalData : []);
        } catch (err) {
            toast.error('🚨 Falha no Uplink de Auditoria.');
        } finally { setIsLoading(false); }
    }, [userRole, selectedEntity, statusFilter]);

    useEffect(() => {
        if (userRole) fetchImoveis();
    }, [userRole, selectedEntity, statusFilter, fetchImoveis]);

    // 🔍 FILTRAGEM LOCAL DE PRECISÃO
    const imoveisFiltrados = useMemo(() => {
        return imoveis.filter(imovel => {
            const searchLower = searchTerm.toLowerCase();
            const matchSearch = 
                imovel.codigoImovel?.toLowerCase().includes(searchLower) ||
                imovel.titulo?.toLowerCase().includes(searchLower) ||
                (imovel.localizacao?.bairro || imovel.bairro || "").toLowerCase().includes(searchLower);
                
            const matchType = typeFilter === 'todos' || imovel.tipoPrincipal === typeFilter;
            return matchSearch && matchType;
        });
    }, [imoveis, searchTerm, typeFilter]);

    const handleImovelAction = async (id, action, imovelData) => {
        const canManage = ['MASTER', 'ADM', 'GESTOR'].includes(userRole);
        if (action === 'edit') { setImovelAlvoId(id); setIsEditorModalOpen(true); return; }
        if (action === 'previa') { setImovelAlvoObj(imovelData); setIsPreviewModalOpen(true); return; }

        if (action === 'aprovar' && canManage) {
            try {
                await apiClient.post(`/api/imoveis/${id}/aprovar`);
                toast.success('🚀 ATIVO HOMOLOGADO!');
                fetchImoveis();
            } catch (err) { toast.error("Erro no protocolo."); }
        }

        if (action === 'incinerar' && canManage) {
            if (!window.confirm(`🔥 CONFIRMAR INCINERAÇÃO: ${imovelData.codigoImovel}?`)) return;
            try {
                await apiClient.delete(`/api/imoveis/${id}/incinerar`);
                toast.warn("💥 Ativo removido do radar.");
                fetchImoveis();
            } catch (err) { toast.error("Falha na operação."); }
        }
    };

    return (
        <MobileGuard>
        <MainLayout>
            <div className="min-h-screen transition-all duration-500 pb-40" style={{ backgroundColor: 'var(--bg-main)' }}>
                <div className="max-w-[1600px] mx-auto p-8 md:p-12 space-y-12">
                    
                    <header className="flex flex-col gap-10 border-b pb-12" style={{ borderColor: 'var(--border-color)' }}>
                        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-8">
                            <div className="flex items-center gap-8">
                                <div className="w-20 h-20 rounded-[2.2rem] border shadow-2xl flex items-center justify-center bg-[var(--bg-card)] border-[var(--border-color)] text-accent transition-all hover:rotate-12">
                                    <FiActivity size={32} className={statusFilter === 'Pendente' ? 'animate-pulse' : ''} />
                                </div>
                                <div>
                                    <h1 className="text-4xl md:text-5xl font-black tracking-tighter uppercase text-auto mb-2 leading-none">
                                        Audit de <span className="text-accent">Ativos Gold</span>
                                    </h1>
                                    <div className="flex items-center gap-4">
                                        <div className="px-3 py-1 rounded-lg bg-accent/10 border border-accent/20 flex items-center gap-2">
                                            <FiShield className="text-accent" size={10} />
                                            <span className="text-[9px] font-black uppercase text-accent tracking-widest">Auditoria Full-Sync</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-4 flex-1 max-w-6xl">
                                <div className="relative flex-1 min-w-[280px] group">
                                    <FiSearch className="absolute left-5 top-1/2 -translate-y-1/2 text-accent opacity-30 group-focus-within:opacity-100 transition-opacity" />
                                    <input 
                                        type="text"
                                        placeholder="Buscar por SKU, Título ou Bairro..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full h-14 pl-14 pr-6 rounded-2xl border border-[var(--border-color)] bg-[var(--bg-card)] text-auto text-xs font-bold outline-none focus:border-accent transition-all shadow-inner"
                                    />
                                </div>
                                
                                <div className="relative w-full md:w-56">
                                    <FiClock className="absolute left-5 top-1/2 -translate-y-1/2 text-accent opacity-40" />
                                    <select 
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        className="w-full h-14 pl-14 pr-10 rounded-2xl border border-accent/30 bg-[var(--bg-card)] text-auto text-[10px] font-black uppercase tracking-widest outline-none appearance-none cursor-pointer focus:border-accent shadow-xl"
                                    >
                                        <option value="Pendente">🕒 Triagem Ativa</option>
                                        <option value="Aprovado">✅ Publicados</option>
                                        <option value="todos">🌍 Visão Global (Tudo)</option>
                                    </select>
                                    <FiChevronDown className="absolute right-5 top-1/2 -translate-y-1/2 opacity-30 pointer-events-none" />
                                </div>

                                <div className="relative w-full md:w-48">
                                    <FiLayers className="absolute left-5 top-1/2 -translate-y-1/2 text-accent opacity-30" />
                                    <select 
                                        value={typeFilter}
                                        onChange={(e) => setTypeFilter(e.target.value)}
                                        className="w-full h-14 pl-14 pr-10 rounded-2xl border border-[var(--border-color)] bg-[var(--bg-card)] text-auto text-[10px] font-black uppercase tracking-widest outline-none appearance-none cursor-pointer focus:border-accent shadow-inner"
                                    >
                                        <option value="todos">Todos Tipos</option>
                                        <option value="Residencial">Residencial</option>
                                        <option value="Comercial">Comercial</option>
                                    </select>
                                    <FiChevronDown className="absolute right-5 top-1/2 -translate-y-1/2 opacity-30 pointer-events-none" />
                                </div>

                                {(userRole === 'MASTER' || userRole === 'ADM') && (
                                    <div className="relative w-full md:w-64">
                                        <FiUsers className="absolute left-5 top-1/2 -translate-y-1/2 text-accent" />
                                        <select 
                                            value={selectedEntity} 
                                            onChange={(e) => setSelectedEntity(e.target.value)}
                                            className="w-full h-14 pl-14 pr-10 rounded-2xl border border-[var(--border-color)] bg-[var(--bg-card)] text-auto text-[10px] font-black uppercase tracking-widest outline-none appearance-none cursor-pointer focus:border-accent shadow-inner"
                                        >
                                            <option value="todos">🌍 Toda a Rede</option>
                                            {entities.map(en => <option key={en._id} value={en._id}>{en.nome}</option>)}
                                        </select>
                                        <FiChevronDown className="absolute right-5 top-1/2 -translate-y-1/2 opacity-30 pointer-events-none" />
                                    </div>
                                )}
                            </div>
                        </div>
                    </header>

                    {isLoading ? (
                        <div className="py-40 text-center flex flex-col items-center justify-center">
                            <FiCpu className="animate-spin mb-6 text-accent opacity-20" size={60} />
                            <p className="text-[10px] font-black uppercase tracking-[0.6em] text-auto opacity-30">Sincronizando Radar...</p>
                        </div>
                    ) : imoveisFiltrados.length === 0 ? (
                        <div className="p-32 rounded-[3rem] border border-dashed border-[var(--border-color)] text-center opacity-50 bg-[var(--bg-card)] animate-in zoom-in-95 duration-500">
                            <FiAlertTriangle className="mx-auto mb-6 text-accent/30" size={48} />
                            <h2 className="text-2xl font-black uppercase tracking-tighter mb-2 text-auto">Ativo não Localizado</h2>
                            <p className="text-[10px] font-bold uppercase tracking-widest opacity-40 italic text-auto">O scanner não detectou registros para esta configuração.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
                            {imoveisFiltrados.map(imovel => (
                                <ImovelCard 
                                    key={imovel._id} 
                                    imovel={imovel} 
                                    onAction={(id, act) => handleImovelAction(id, act, imovel)}
                                    userRole={userRole} 
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* MODAIS INTEGRADOS */}
            {isEditorModalOpen && <ImovelEditorModal imovelId={imovelAlvoId} onClose={() => setIsEditorModalOpen(false)} onUpdateSuccess={fetchImoveis} />}
            {isPreviewModalOpen && imovelAlvoObj && <PreviewVitrinePremiumModal imovel={imovelAlvoObj} onClose={() => setIsPreviewModalOpen(false)} />}
        </MainLayout>
        </MobileGuard>
    );
}